import React, { useEffect, useState } from "react";
import { api, formatFCFA } from "@/lib/api";

function Card({ label, value, accent = false, testId, hint = null }) {
  return (
    <div className={`border ${accent ? "border-[#B8860B]/50" : "border-black/5"} bg-white p-6`} data-testid={testId}>
      <div className="label-tiny">{label}</div>
      <div className={`mt-2 font-display text-2xl lg:text-3xl ${accent ? "text-[#B8860B]" : "text-[#1A1A1A]"}`}>
        {value}
      </div>
      {hint && <div className="mt-2 text-[10px] text-[#6B6B6B] italic leading-tight">{hint}</div>}
    </div>
  );
}

function BlockTitle({ title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="label-tiny flex items-center gap-3 mb-2">
        <span className="gold-line" /> {title}
      </div>
      {subtitle && <p className="text-xs text-[#6B6B6B] italic">{subtitle}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setError("Impossible de charger le tableau de bord. Réessayez."));
  }, []);
  if (error) return <div className="text-red-700 border border-red-300 bg-red-50 p-6" data-testid="dash-error">{error}</div>;
  if (!data) return <div className="text-[#6B6B6B]">Chargement...</div>;

  const s = data.stock_summary || {};
  const d = data.delivered_summary || {};

  return (
    <div>
      <h1 className="font-display text-3xl lg:text-4xl mb-2">Tableau de bord</h1>
      <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mb-10">Vue d'ensemble en temps réel</p>

      {/* 1. STOCK ACTUEL */}
      <section className="mb-12" data-testid="block-stock-summary">
        <BlockTitle
          title="1. Stock actuel"
          subtitle="Valeur du stock au moment présent (mise à jour instantanée à chaque commande / réapprovisionnement)."
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card label="Articles en stock" value={s.total_pieces} testId="stock-pieces" />
          <Card label="Coût total de revient" value={formatFCFA(s.cost_value)} testId="stock-cost" hint="Quantité × Coût de revient" />
          <Card label="Valeur au prix de vente" value={formatFCFA(s.sale_value)} testId="stock-sale" hint="Quantité × Prix de vente" accent />
          <Card label="Marge potentielle" value={formatFCFA(s.potential_margin)} testId="stock-margin" accent hint="Valeur vente − Coût stock" />
        </div>
      </section>

      {/* 2. COMMANDES LIVRÉES */}
      <section className="mb-12" data-testid="block-delivered-summary">
        <BlockTitle
          title="2. Commandes livrées"
          subtitle="Calculé exclusivement sur les commandes en statut « Livré et notifié ». Chaque commande livrée est figée : prix de vente et de revient snapshottés au moment de la livraison."
        />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card label="Commandes livrées" value={d.orders_count} testId="delivered-orders" />
          <Card label="Articles vendus" value={d.items_sold} testId="delivered-items" />
          <Card label="Coût total revient" value={formatFCFA(d.total_cost)} testId="delivered-cost" hint="Quantité × coût snapshotté" />
          <Card label="Montant encaissé" value={formatFCFA(d.total_revenue)} testId="delivered-revenue" accent hint="Hors livraison" />
          <Card label="Marge réalisée" value={formatFCFA(d.total_margin)} testId="delivered-margin" accent hint="Vente − coût de revient" />
        </div>
      </section>

      {/* 3. ACTIVITÉ */}
      <section className="mb-12" data-testid="block-activity">
        <BlockTitle title="3. Activité" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card label="Total commandes" value={data.total_orders} testId="dash-orders" />
          <Card label="Non livrés" value={data.non_livres} testId="dash-non-livres" />
          <Card label="Taux de livraison" value={`${data.delivery_rate.toFixed(1)}%`} testId="dash-delivery-rate" hint="Livrés / (Livrés + Non livrés)" />
          <Card label="Panier moyen" value={formatFCFA(data.average_basket)} testId="dash-basket" />
          <Card label="Précommandes" value={data.preorders_count} testId="dash-preorders" />
          <Card label="Produits référencés" value={data.products_count} testId="dash-products" />
          <Card label="Taux de rupture" value={`${data.rupture_rate.toFixed(1)}%`} testId="dash-rupture" />
          <Card label="Jamais vendus" value={data.never_sold.length} testId="dash-never-sold" />
        </div>
      </section>

      {/* Satisfaction */}
      <div className="mb-10 border border-[#B8860B]/40 bg-white p-6" data-testid="satisfaction-breakdown">
        <h3 className="font-display text-xl text-[#B8860B] mb-4">Répartition des avis clients</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="border border-black/5 p-4">
            <div className="text-2xl font-display text-[#B8860B]">⭐ {data.satisfaction_counts?.tres_satisfait || 0}</div>
            <div className="text-xs text-[#6B6B6B] uppercase tracking-widest mt-1">Très satisfait(e)</div>
          </div>
          <div className="border border-black/5 p-4">
            <div className="text-2xl font-display text-green-700">🙂 {data.satisfaction_counts?.satisfait || 0}</div>
            <div className="text-xs text-[#6B6B6B] uppercase tracking-widest mt-1">Satisfait(e)</div>
          </div>
          <div className="border border-black/5 p-4">
            <div className="text-2xl font-display text-red-700">😔 {data.satisfaction_counts?.non_satisfait || 0}</div>
            <div className="text-xs text-[#6B6B6B] uppercase tracking-widest mt-1">Non satisfait(e)</div>
          </div>
          <div className="border border-black/5 p-4">
            <div className="text-2xl font-display text-[#1A1A1A]/60">— {data.satisfaction_counts?.sans_avis || 0}</div>
            <div className="text-xs text-[#6B6B6B] uppercase tracking-widest mt-1">Sans avis</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div className="border border-black/5 bg-white p-6">
          <h3 className="font-display text-xl text-[#B8860B] mb-4">Meilleures ventes</h3>
          {data.best_sellers.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">Aucune vente confirmée pour le moment.</p>
          ) : (
            <ul className="divide-y divide-black/5">
              {data.best_sellers.map((p) => (
                <li key={p.id} className="py-3 flex justify-between text-sm">
                  <span>{p.name}</span>
                  <span className="text-[#B8860B]">{p.sales_count} vendu(s)</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-black/5 bg-white p-6">
          <h3 className="font-display text-xl mb-4">Stock faible</h3>
          {data.low_stock.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">Aucun stock critique.</p>
          ) : (
            <ul className="divide-y divide-black/5">
              {data.low_stock.map((p) => (
                <li key={p.id} className="py-3 flex justify-between text-sm">
                  <span>{p.name}</span>
                  <span className={p.stock < 2 ? "text-red-700" : "text-orange-700"}>
                    {p.stock} restant{p.stock > 1 ? "s" : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-10 border border-[#B8860B]/40 bg-white p-6">
        <h3 className="font-display text-xl text-[#B8860B] mb-4">Exportations</h3>
        <div className="flex flex-wrap gap-3">
          {["products", "orders", "preorders", "customers", "financials"].map((k) => (
            <React.Fragment key={k}>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${k}.xlsx`}
                className="btn-secondary"
                data-testid={`export-xlsx-${k}`}
                onClick={(e) => {
                  e.preventDefault();
                  const token = localStorage.getItem("rdf_admin_token");
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${k}.xlsx`, { headers: { Authorization: `Bearer ${token}` } })
                    .then((r) => r.blob())
                    .then((b) => { const u = URL.createObjectURL(b); window.open(u); });
                }}
              >
                {k}.xlsx
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${k}.pdf`}
                className="btn-secondary"
                data-testid={`export-pdf-${k}`}
                onClick={(e) => {
                  e.preventDefault();
                  const token = localStorage.getItem("rdf_admin_token");
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${k}.pdf`, { headers: { Authorization: `Bearer ${token}` } })
                    .then((r) => r.blob())
                    .then((b) => { const u = URL.createObjectURL(b); window.open(u); });
                }}
              >
                {k}.pdf
              </a>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
